<template>
  <Transition name="fade">
    <Dialog
      :title="modalTitle"
      :show-cancel-button="false"
      :show-save-button="false"
      :custom-footer="true"
      :show-footer="true"
      :show-close-button="true"
      :sm="true"
      @closeEvent="closeEvent"
    >
      <template #modal-content>
        <!-- Shipping Date Section -->
        <div v-if="showShippingDate" class="space-y-4">
          <h3 class="text-lg font-semibold text-ink">Estimated shipping date</h3>
          <div>
            <label class="block text-sm font-medium text-ink-soft mb-2"
              >Date <span class="text-red-600 font-normal">*</span></label
            >
            <div class="w-full max-w-full">
              <Datepicker
                class="w-full"
                v-model="est_shipping_date"
                :min-date="new Date()"
                format="dd/MM/yyyy"
                placeholder="Choose a date"
              />
            </div>
          </div>
          <div>
            <label class="block text-sm font-medium text-ink-soft mb-2"
              >Reason for change <span class="text-red-600 font-normal">*</span></label
            >
            <select
              v-model="shipping_date_change_reason_code"
              data-testid="shipping-date-reason-code"
              class="w-full px-4 py-2 text-sm text-ink border border-line rounded-lg bg-surface focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="" disabled>Select a reason</option>
              <option
                v-for="reason in reason_code_options"
                :key="reason.code"
                :value="reason.code"
              >
                {{ reason.label }}
              </option>
            </select>

            <!-- AB#21519: mandatory when the custom option is picked, optional extra detail otherwise. -->
            <div class="mt-3">
              <label class="block text-xs font-medium text-ink-soft mb-1">
                {{ isCustomShippingDateReason ? 'Custom reason' : 'Additional detail' }}
                <span v-if="isCustomShippingDateReason" class="text-red-600 font-normal">*</span>
                <span v-else class="text-ink-mute font-normal">(optional)</span>
              </label>
              <textarea
                v-model="shipping_date_change_reason"
                data-testid="shipping-date-reason-detail"
                rows="3"
                class="w-full min-h-[4.5rem] px-4 py-2 text-sm text-ink border border-line rounded-lg bg-surface placeholder:text-ink-mute focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-y"
                :placeholder="
                  isCustomShippingDateReason
                    ? 'Write the reason in your own words'
                    : 'Anything the customer should know'
                "
              />
            </div>
            <p class="text-xs text-ink-mute mt-2 leading-relaxed">
              Shown on the order timeline next to this shipping date.
            </p>
          </div>
        </div>

        <!-- Put In Production Section -->
        <div v-if="showPutInProduction" class="space-y-4">
          <h3 class="text-lg font-semibold text-ink">Put order in production</h3>
          <div>
            <label class="block text-sm font-medium text-ink-soft mb-2"
              >Estimated shipping date <span class="text-red-600 font-normal">*</span></label
            >
            <div class="w-full max-w-full">
              <Datepicker
                class="w-full"
                v-model="est_shipping_date"
                :min-date="new Date()"
                format="dd/MM/yyyy"
                placeholder="Choose a date"
              />
            </div>
          </div>
          <div>
            <label class="block text-sm font-medium text-ink-soft mb-2"
              >Reason for change <span class="text-red-600 font-normal">*</span></label
            >
            <select
              v-model="shipping_date_change_reason_code"
              data-testid="shipping-date-reason-code"
              class="w-full px-4 py-2 text-sm text-ink border border-line rounded-lg bg-surface focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="" disabled>Select a reason</option>
              <option
                v-for="reason in reason_code_options"
                :key="reason.code"
                :value="reason.code"
              >
                {{ reason.label }}
              </option>
            </select>

            <!-- AB#21519: mandatory when the custom option is picked, optional extra detail otherwise. -->
            <div class="mt-3">
              <label class="block text-xs font-medium text-ink-soft mb-1">
                {{ isCustomShippingDateReason ? 'Custom reason' : 'Additional detail' }}
                <span v-if="isCustomShippingDateReason" class="text-red-600 font-normal">*</span>
                <span v-else class="text-ink-mute font-normal">(optional)</span>
              </label>
              <textarea
                v-model="shipping_date_change_reason"
                data-testid="shipping-date-reason-detail"
                rows="3"
                class="w-full min-h-[4.5rem] px-4 py-2 text-sm text-ink border border-line rounded-lg bg-surface placeholder:text-ink-mute focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-y"
                :placeholder="
                  isCustomShippingDateReason
                    ? 'Write the reason in your own words'
                    : 'e.g. agreed timeline with the factory'
                "
              />
            </div>
            <p class="text-xs text-ink-mute mt-2 leading-relaxed">
              Shown on the order timeline next to this shipping date.
            </p>
          </div>
        </div>

        <!-- Shipping Details Section -->
        <div v-if="showShippingDetails" class="mb-6">
          <!-- Show existing shipping details if available (not in move-order confirm mode) -->
          <div
            v-if="orderItem?.tracking_no && !editShippingLink && !submitEmitsOnly"
            class="mb-4 p-3 bg-surface-2 rounded-md"
          >
            <div class="text-sm text-ink-soft mb-2">Current Shipping Details:</div>
            <div class="font-medium">{{ orderItem.shipping_company }}</div>
            <div>
              <a
                v-if="orderItem.tracking_link"
                :href="orderItem.tracking_link"
                target="_blank"
                class="text-blue-600 hover:underline"
              >
                {{ orderItem.tracking_no }}
              </a>
              <span v-else>{{ orderItem.tracking_no }}</span>
            </div>
            <!-- MAWB details in read-only view -->
            <template v-if="orderItem.mawb_number">
              <div class="mt-2 pt-2 border-t border-line text-sm space-y-1">
                <div>
                  <span class="text-ink-soft">Master Airway Bill (MAWB):</span>
                  <span class="font-medium ml-1">{{ orderItem.mawb_number }}</span>
                </div>
                <div v-if="orderItem.mawb_airline">
                  <span class="text-ink-soft">Airline:</span>
                  <span class="font-medium ml-1">{{ orderItem.mawb_airline }}</span>
                </div>
                <div>
                  <a
                    :href="`https://www.track-trace.com/aircargo?awb=${orderItem.mawb_number.replace('-', '')}`"
                    target="_blank"
                    class="text-blue-600 hover:underline"
                  >
                    Track Shipment
                  </a>
                </div>
              </div>
            </template>
          </div>
          <!-- Shipping Details Form (move flow: always show form, pre-filled when tracking exists) -->
          <div
            v-if="!orderItem?.tracking_no || editShippingLink || submitEmitsOnly"
            class="space-y-3"
          >
            <div>
              <label class="block text-sm font-medium text-ink-soft mb-2"
                >Shipping company <span class="text-red-600 font-normal">*</span></label
              >
              <select
                v-model="shipping_company"
                required
                @change="setTrackingLink(($event.target as HTMLSelectElement).value, 'company')"
                class="w-full px-4 py-2 text-sm border border-line rounded-lg bg-surface focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                <option value="">Select shipping company</option>
                <option v-for="option in shippingOptions" :key="option.value" :value="option.value">
                  {{ option.text }}
                </option>
              </select>
            </div>

            <div>
              <label class="block text-sm font-medium text-ink-soft mb-2"
                >Tracking number <span class="text-red-600 font-normal">*</span></label
              >
              <input
                v-model="tracking_no"
                required
                @input="setTrackingLink(($event.target as HTMLInputElement).value, 'number')"
                type="text"
                placeholder="Enter tracking number"
                class="w-full px-4 py-2 text-sm border border-line rounded-lg bg-surface placeholder:text-ink-mute focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>

            <div v-if="editShippingLink || showShippingDetails">
              <label class="block text-sm font-medium text-ink-soft mb-2"
                >Tracking link (auto-generated or manual)
                <span class="text-red-600 font-normal">*</span></label
              >
              <input
                v-model="tracking_link"
                type="url"
                required
                placeholder="Will be auto-generated when company and tracking number are entered"
                class="w-full px-4 py-2 text-sm border border-line rounded-lg bg-surface placeholder:text-ink-mute focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
              <p v-if="tracking_link" class="text-xs text-ink-mute mt-1">
                Auto-generated tracking link. You can edit this manually if needed.
              </p>
            </div>

            <!-- MAWB section — only revealed after a tracking number has been entered -->
            <template v-if="tracking_no">
              <div>
                <label class="block text-sm text-ink-soft mb-1">Master Airway Bill (MAWB)</label>
                <input
                  v-model="mawb_number"
                  @input="handleMawbInput"
                  @blur="handleMawbBlur"
                  type="text"
                  placeholder="e.g. 235-12345678"
                  class="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  :class="mawbError ? 'border-red-500' : 'border-line'"
                />
                <p v-if="mawbError" class="text-xs text-red-500 mt-1">{{ mawbError }}</p>
              </div>

              <!-- Airline dropdown — shown only when MAWB number is present -->
              <div v-if="mawb_number">
                <label class="block text-sm text-ink-soft mb-1">Airline</label>
                <select
                  v-model="mawb_airline_prefix"
                  :disabled="isLoadingAirlines"
                  class="w-full p-2 border border-line rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  <option value="">
                    {{ isLoadingAirlines ? 'Loading airlines...' : 'Select airline' }}
                  </option>
                  <option
                    v-for="airline in mawb_airlines"
                    :key="airline.prefix"
                    :value="airline.prefix"
                  >
                    {{ airline.prefix }} - {{ airline.name }}
                  </option>
                </select>
                <p v-if="airlineHint" class="text-xs text-ink-mute mt-1">{{ airlineHint }}</p>
              </div>

              <!-- Auto-generated MAWB tracking link — read-only, derived from MAWB number -->
              <div v-if="mawbTrackingUrl">
                <label class="block text-sm text-ink-soft mb-1">MAWB Tracking Link</label>
                <a
                  :href="mawbTrackingUrl"
                  target="_blank"
                  class="text-sm text-blue-600 hover:underline break-all"
                >
                  {{ mawbTrackingUrl }}
                </a>
              </div>
            </template>

            <div
              v-if="
                showShippingDetails &&
                allowSkipSavingShippingOption &&
                (!orderItem?.tracking_no || editShippingLink || submitEmitsOnly)
              "
              class="flex items-start gap-2 pt-2 border-t border-line-soft"
            >
              <input
                id="skip-shipping-save"
                v-model="skipSavingShippingDetails"
                type="checkbox"
                class="mt-1 rounded border-line"
              />
              <label
                for="skip-shipping-save"
                class="text-sm text-ink-soft cursor-pointer leading-snug"
              >
                Skip saving tracking details (order status still updates; tracking is not stored on
                the item or sent to integrations).
              </label>
            </div>
          </div>
        </div>
      </template>
      <template #footer>
        <!-- Shipping Date Footer (matches Shared/Dialog + DeliveryRequestModal) -->
        <div v-if="showShippingDate" class="flex flex-wrap gap-2 justify-end items-center">
          <button
            type="button"
            @click="closeEvent"
            class="px-3 py-2 rounded-md bg-surface-3 text-ink-soft hover:brightness-95"
          >
            Cancel
          </button>
          <button
            type="button"
            @click="saveShippingDate(false)"
            class="px-3 py-2 rounded-md bg-surface border border-line text-ink-soft text-sm font-medium hover:bg-surface-2"
          >
            Save without notifying
          </button>
          <button
            type="button"
            @click="saveShippingDate(true)"
            class="px-3 py-2 rounded-md bg-primary text-white text-sm font-medium hover:bg-primary-dark"
          >
            Save and notify
          </button>
        </div>

        <!-- Put In Production Footer -->
        <div v-if="showPutInProduction" class="flex flex-wrap gap-2 justify-end items-center">
          <button
            type="button"
            @click="closeEvent"
            class="px-3 py-2 rounded-md bg-surface-3 text-ink-soft hover:brightness-95"
          >
            Cancel
          </button>
          <button
            type="button"
            @click="savePutInProduction(false)"
            class="px-3 py-2 rounded-md bg-surface border border-line text-ink-soft text-sm font-medium hover:bg-surface-2"
          >
            Save without notifying
          </button>
          <button
            type="button"
            @click="savePutInProduction(true)"
            class="px-3 py-2 rounded-md bg-primary text-white text-sm font-medium hover:bg-primary-dark"
          >
            Save and notify
          </button>
        </div>

        <!-- Shipping Details Footer -->
        <div v-if="showShippingDetails" class="flex flex-wrap gap-2 justify-end items-center">
          <button
            type="button"
            @click="closeEvent"
            class="px-3 py-2 rounded-md bg-surface-3 text-ink-soft hover:brightness-95"
          >
            {{ orderItem?.tracking_no && !submitEmitsOnly ? 'Close' : 'Cancel' }}
          </button>
          <button
            v-if="!orderItem?.tracking_no || editShippingLink || submitEmitsOnly"
            type="button"
            @click="saveShippingDetails"
            class="px-3 py-2 rounded-md bg-primary text-white text-sm font-medium hover:bg-primary-dark"
          >
            {{ submitEmitsOnly ? 'Continue' : !editShippingLink ? 'Save' : 'Update' }}
          </button>
        </div>
      </template>
    </Dialog>
  </Transition>
</template>
<script setup lang="ts">
import Datepicker from '@/components/Shared/Datepicker.vue';
import Dialog from '@/components/Shared/Dialog.vue';
import { showAlert, showToast } from '@/composables/swal';
import { http } from '@/interceptors/axiosRequestInterceptor';
import { OrderDetailItem } from '@/models';
import { parseMawbAirlinesResponse } from '@/utils/parseMawbAirlinesResponse';
import {
  CUSTOM_SHIPPING_DATE_REASON_CODE,
  SHIPPING_DATE_REASON_DETAIL_MIN_LENGTH,
  shippingDateChangeReasons,
  type ShippingDateChangeReason,
} from '@/utils/constants/shippingDateChangeReasons';
import { parseShippingDateReasonCodesResponse } from '@/utils/parseShippingDateReasonCodesResponse';
import { format, isBefore, isValid, parseISO, startOfDay } from 'date-fns';
import { computed, ref, watch } from 'vue';

export interface ShippingDetailsFormData {
  shipping_company: string;
  tracking_no: string;
  tracking_link: string;
  mawb_number?: string | null;
  mawb_airline?: string | null;
  skip_saving_shipping_details?: boolean;
}
interface Props {
  showShippingDate?: boolean;
  showPutInProduction?: boolean;
  showShippingDetails?: boolean;
  orderItem?: OrderDetailItem;
  order?: any;
  status?: string;
  est_shipping_date?: Date | string | null;
  editShippingLink?: boolean;
  /** When true, shipping confirm only validates and emits submit + close (no parent shippingDetailsSaved / no save toasts). */
  submitEmitsOnly?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  showShippingDate: false,
  showPutInProduction: false,
  showShippingDetails: false,
  orderItem: null,
  order: null,
  status: '',
  est_shipping_date: null,
  editShippingLink: false,
  submitEmitsOnly: false,
});

const emit = defineEmits<{
  close: [];
  submit: [formData: ShippingDetailsFormData];
  shippingDateSaved: [notify: boolean];
  putInProductionSaved: [notify: boolean];
  shippingDetailsSaved: [details: any];
}>();

const est_shipping_date = ref<Date | string | null>(props.est_shipping_date);
const shipping_dates = ref<string[]>([]);
const shipping_date_change_reason = ref('');
// AB#21519: the structured reason code. '' until the user picks one — the API rejects
// anything that is not in App\Enums\ShippingDateChangeReason, so it is never free-form.
const shipping_date_change_reason_code = ref('');

/**
 * AB#21521 — the list the dropdown offers. Seeded from the bundled constant so the modal is
 * usable immediately and still works if the endpoint is unreachable (an older API, a blip),
 * then replaced by what the backend enum actually accepts. The enum is the source of truth:
 * realigning the vocabulary with the customer's sheet is a change there alone.
 */
const reason_code_options = ref<ShippingDateChangeReason[]>(shippingDateChangeReasons);
let reasonCodesLoaded = false;

const loadReasonCodes = async () => {
  if (reasonCodesLoaded) return;
  reasonCodesLoaded = true;
  try {
    const response = await http.get('/shipping-date-reason-codes');
    const parsed = parseShippingDateReasonCodesResponse(response.data);
    if (parsed.length > 0) reason_code_options.value = parsed;
  } catch (e) {
    // Keep the bundled list — an out-of-date list beats a dropdown the user cannot use.
    console.error('Failed to load shipping date reason codes:', e);
  }
};

const isCustomShippingDateReason = computed(() => {
  const code = shipping_date_change_reason_code.value;
  // Prefer what the served list says, so a future code that needs free text works without a
  // frontend release; fall back to the one code we know carries no meaning on its own.
  const option = reason_code_options.value.find((reason) => reason.code === code);
  return option ? option.requiresDetail : code === CUSTOM_SHIPPING_DATE_REASON_CODE;
});

// Putting an order into production sets the FIRST estimate rather than moving one, so
// that reason is preselected there; the user can still change it.
watch(
  () => props.showPutInProduction,
  (showing) => {
    if (showing && !shipping_date_change_reason_code.value) {
      shipping_date_change_reason_code.value = 'initial_estimate';
    }
  },
  { immediate: true }
);

watch(
  () => [props.showShippingDate, props.showPutInProduction],
  ([showDate, showProduction]) => {
    if (showDate || showProduction) loadReasonCodes();
  },
  { immediate: true }
);

/**
 * Validates the reason pair and returns what to post, or null once it has alerted.
 * A code is always required; the free text is required only for the custom option and
 * is optional extra detail alongside any standard code.
 */
const shippingDateReasonPayload = (): {
  change_reason_code: string;
  change_reason?: string;
} | null => {
  const code = shipping_date_change_reason_code.value;
  if (!code) {
    showAlert('ERROR', 'Please select a reason for this shipping date.', 'error');
    return null;
  }

  const detail = shipping_date_change_reason.value.trim();
  if (isCustomShippingDateReason.value && detail.length < SHIPPING_DATE_REASON_DETAIL_MIN_LENGTH) {
    showAlert(
      'ERROR',
      `Please write the custom reason (at least ${SHIPPING_DATE_REASON_DETAIL_MIN_LENGTH} characters).`,
      'error'
    );
    return null;
  }

  return detail
    ? { change_reason_code: code, change_reason: detail }
    : { change_reason_code: code };
};
const shipping_company = ref('');
const tracking_no = ref('');
const tracking_link = ref('');
const skipSavingShippingDetails = ref(false);

const syncShippingFieldsFromOrderItem = () => {
  const item = props.orderItem;
  if (!item) {
    shipping_company.value = '';
    tracking_no.value = '';
    tracking_link.value = '';
    return;
  }
  const co = item.shipping_company;
  shipping_company.value = co == null || co === '' ? '' : String(co).toLowerCase().trim();
  tracking_no.value = item.tracking_no != null ? String(item.tracking_no).trim() : '';
  tracking_link.value = item.tracking_link != null ? String(item.tracking_link).trim() : '';
};

watch(() => props.orderItem, syncShippingFieldsFromOrderItem, { immediate: true });

watch(
  () =>
    [
      props.orderItem?.id,
      props.order?.id,
      (props.order as { company?: { platform?: string } } | undefined)?.company?.platform,
      (props.order as { ecommerce_order_id?: unknown } | undefined)?.ecommerce_order_id,
      props.showShippingDetails,
      props.editShippingLink,
      props.submitEmitsOnly,
    ] as const,
  () => {
    skipSavingShippingDetails.value = false;
  }
);

const orderHasLinkedEcommerce = (o: Record<string, unknown> | null | undefined): boolean => {
  if (!o) return false;
  const id = o.ecommerce_order_id;
  if (id == null || id === '') return false;
  return String(id).trim().length > 0;
};

const allowSkipSavingShippingOption = computed(() => {
  const o = props.order as Record<string, unknown> | undefined;
  if (!o) return true;
  const company = o.company as { platform?: string } | undefined;
  const platform = String(company?.platform ?? '').toLowerCase();
  if (platform === 'shopify' || platform === 'wordpress' || platform === 'bigcommerce')
    return false;
  if (orderHasLinkedEcommerce(o)) return false;
  return true;
});

watch(allowSkipSavingShippingOption, (allow) => {
  if (!allow) skipSavingShippingDetails.value = false;
});

// MAWB state — select uses API `prefix` (unique); name is resolved when saving (duplicate names in API are OK)
const mawb_number = ref<string>(props.orderItem?.mawb_number || '');
const mawb_airline_prefix = ref<string>('');
const mawb_airlines = ref<{ prefix: string; name: string }[]>([]);
const isLoadingAirlines = ref(false);
const mawbError = ref('');
const airlineHint = ref('');
/** First 3 digits of MAWB last time we auto-matched; avoids re-detect overwriting a manual airline on edit. */
const lastMawbPrefixDigits = ref<string>('');

const MAWB_REGEX = /^\d{3}-\d{8}$/;

/** Prefer saved airline name (manual choice); tie-break duplicate names with MAWB prefix; else MAWB prefix. */
function resolveMawbPrefixFromOrder(
  airlines: { prefix: string; name: string }[],
  savedName: string | null | undefined,
  pFromMawb: string
): string {
  const trimmed = (savedName ?? '').trim();
  if (trimmed) {
    const matches = airlines.filter((a) => a.name === trimmed);
    if (matches.length === 1) return matches[0].prefix;
    if (matches.length > 1 && pFromMawb) {
      const byMawb = matches.find((a) => a.prefix === pFromMawb);
      return byMawb ? byMawb.prefix : matches[0].prefix;
    }
    const norm = trimmed.replace(/\s+/g, ' ');
    const fuzzy = airlines.filter((a) => a.name.replace(/\s+/g, ' ') === norm);
    if (fuzzy.length === 1) return fuzzy[0].prefix;
  }
  if (pFromMawb && airlines.some((a) => a.prefix === pFromMawb)) return pFromMawb;
  return '';
}

// Auto-generated read-only — not stored separately, derived from mawb_number on the fly
const mawbTrackingUrl = computed(() => {
  if (!mawb_number.value || !MAWB_REGEX.test(mawb_number.value)) return '';
  return 'https://www.track-trace.com/aircargo?awb=' + mawb_number.value.replace('-', '');
});

const loadAirlines = async () => {
  try {
    isLoadingAirlines.value = true;
    const response = await http.get('/get-mawb-airlines');
    // Full axios body: { success, message, result: [...] } — normalize prefix/name types
    mawb_airlines.value = parseMawbAirlinesResponse(response.data);
  } catch (e) {
    console.error('Failed to load MAWB airlines:', e);
    mawb_airlines.value = [];
    showToast('Could not load airline list', 'error');
  } finally {
    isLoadingAirlines.value = false;
  }
};

const syncMawbFromOrderItem = () => {
  if (!props.orderItem) return;
  mawb_number.value = props.orderItem.mawb_number || '';
  airlineHint.value = '';

  const pFromMawb =
    mawb_number.value && MAWB_REGEX.test(mawb_number.value)
      ? mawb_number.value.substring(0, 3)
      : '';

  if (!mawb_number.value || !MAWB_REGEX.test(mawb_number.value)) {
    mawb_airline_prefix.value = '';
    lastMawbPrefixDigits.value = '';
    return;
  }

  lastMawbPrefixDigits.value = pFromMawb;
  mawb_airline_prefix.value = resolveMawbPrefixFromOrder(
    mawb_airlines.value,
    props.orderItem.mawb_airline,
    pFromMawb
  );
};

const detectAirlineFromPrefix = (prefix: string) => {
  const match = mawb_airlines.value.find((a) => a.prefix === prefix);
  if (match) {
    mawb_airline_prefix.value = match.prefix;
    airlineHint.value = '';
  } else {
    mawb_airline_prefix.value = '';
    airlineHint.value = 'Airline not detected — please select manually';
  }
};

const handleMawbInput = () => {
  mawbError.value = '';
  if (!mawb_number.value) {
    mawb_airline_prefix.value = '';
    airlineHint.value = '';
    lastMawbPrefixDigits.value = '';
    return;
  }
  if (mawb_number.value.length === 12 && MAWB_REGEX.test(mawb_number.value)) {
    const p = mawb_number.value.substring(0, 3);
    if (p !== lastMawbPrefixDigits.value) {
      lastMawbPrefixDigits.value = p;
      detectAirlineFromPrefix(p);
    }
  }
};

const handleMawbBlur = () => {
  if (!mawb_number.value) {
    mawbError.value = '';
    return;
  }
  if (!MAWB_REGEX.test(mawb_number.value)) {
    mawbError.value = 'MAWB format must be: 3 digits, hyphen, 8 digits (e.g. 235-12345678)';
    return;
  }
  const p = mawb_number.value.substring(0, 3);
  if (!mawb_airline_prefix.value) {
    lastMawbPrefixDigits.value = p;
    detectAirlineFromPrefix(p);
  }
};

watch(
  () => [props.showShippingDetails, props.orderItem?.id, props.editShippingLink] as const,
  async () => {
    if (!props.showShippingDetails) return;
    await loadAirlines();
    syncMawbFromOrderItem();
  },
  { immediate: true }
);

const shippingOptions = ref([
  { value: 'fedex', text: 'FedEx' },
  { value: 'ups', text: 'UPS' },
  { value: 'usps', text: 'USPS' },
  { value: 'dhl', text: 'DHL' },
]);

// Shipping companies with tracking link templates
const shipping_companies = ref({
  fedex: {
    name: 'FedEx',
    tracking_link: 'https://www.fedex.com/fedextrack/?trknbr={tracking_no}',
  },
  ups: {
    name: 'UPS',
    tracking_link: 'https://www.ups.com/track?tracknum={tracking_no}',
  },
  usps: {
    name: 'USPS',
    tracking_link: 'https://tools.usps.com/go/TrackConfirmAction?tLabels={tracking_no}',
  },
  dhl: {
    name: 'DHL',
    tracking_link: 'https://www.dhl.com/en/express/tracking.html?AWB={tracking_no}',
  },
});

const setTrackingLink = (value: string, type: 'company' | 'number') => {
  let company = null;

  if (type === 'company') {
    company = value;
    shipping_company.value = value;
  } else if (type === 'number') {
    company = shipping_company.value;
    tracking_no.value = value;
  }

  if (company && shipping_companies.value[company] && tracking_no.value) {
    let track_link = shipping_companies.value[company].tracking_link;
    track_link = track_link.replace('{tracking_no}', tracking_no.value);
    tracking_link.value = track_link;
  } else {
    tracking_link.value = '';
  }
};

const modalTitle = computed(() => {
  if (props.showShippingDate) {
    return 'Add Shipping Date';
  }
  if (props.showPutInProduction) {
    return 'Put Order In Production';
  }
  if (props.showShippingDetails) {
    return props.submitEmitsOnly ? 'Confirm shipping details' : 'Enter Shipping Details';
  }
  return 'Shipping Details';
});

const closeEvent = () => {
  shipping_date_change_reason.value = '';
  shipping_date_change_reason_code.value = '';
  skipSavingShippingDetails.value = false;
  emit('close');
};

const saveShippingDate = async (notify: boolean) => {
  if (!est_shipping_date.value) {
    showAlert('ERROR', 'Please select a shipping date', 'error');
    return;
  }

  const reasonPayload = shippingDateReasonPayload();
  if (!reasonPayload) {
    return;
  }

  // Convert Date object to YYYY-MM-DD string format using date-fns
  let formattedDate: string;
  const dateValue = est_shipping_date.value;

  if (dateValue instanceof Date) {
    formattedDate = format(dateValue, 'yyyy-MM-dd');
  } else if (typeof dateValue === 'string') {
    // If it's already a string, ensure it's in the correct format
    const parsedDate = parseISO(dateValue);
    if (!isValid(parsedDate)) {
      showAlert('ERROR', 'Invalid date format', 'error');
      return;
    }
    formattedDate = format(parsedDate, 'yyyy-MM-dd');
  } else {
    showAlert('ERROR', 'Invalid date value', 'error');
    return;
  }

  // Validate that the date is not in the past using date-fns
  const estShippingDate = parseISO(formattedDate);
  const today = startOfDay(new Date());

  if (isBefore(estShippingDate, today)) {
    showAlert('ERROR', 'Estimated shipping date cannot be in the past.', 'error');
    return;
  }

  shipping_dates.value = [...shipping_dates.value, formattedDate];

  let url = `customer-orders/${props.orderItem.id}/est-shipping-date`;

  try {
    await http.post(url, { shipping_dates: shipping_dates.value, ...reasonPayload });

    if (notify) {
      let url = `notify-customer-shipping-date/${props.orderItem.id}/order/${props.order.id}`;
      if (formattedDate) {
        await http.post(url);
        showAlert('Success', 'Shipping date added and customer notified', 'success');
      } else {
        showAlert('error', 'Please save shipping date.', 'error');
      }
    }

    est_shipping_date.value = null;
    shipping_date_change_reason.value = '';
    shipping_date_change_reason_code.value = '';
    showAlert('Success', 'Shipping date added', 'success');
    emit('shippingDateSaved', notify);
    closeEvent();
  } catch (errorResponse) {
    showAlert('ERROR', 'Failed to save shipping date', 'error');
  }
};

const savePutInProduction = async (notify: boolean) => {
  if (!est_shipping_date.value) {
    showAlert('ERROR', 'Please select a shipping date', 'error');
    return;
  }

  const reasonPayload = shippingDateReasonPayload();
  if (!reasonPayload) {
    return;
  }

  // Convert Date object to YYYY-MM-DD string format using date-fns
  let formattedDate: string;
  const dateValue = est_shipping_date.value;

  if (dateValue instanceof Date) {
    formattedDate = format(dateValue, 'yyyy-MM-dd');
  } else if (typeof dateValue === 'string') {
    // If it's already a string, ensure it's in the correct format
    const parsedDate = parseISO(dateValue);
    if (!isValid(parsedDate)) {
      showAlert('ERROR', 'Invalid date format', 'error');
      return;
    }
    formattedDate = format(parsedDate, 'yyyy-MM-dd');
  } else {
    showAlert('ERROR', 'Invalid date value', 'error');
    return;
  }

  // Validate that the date is not in the past using date-fns
  const estShippingDate = parseISO(formattedDate);
  const today = startOfDay(new Date());

  if (isBefore(estShippingDate, today)) {
    showAlert('ERROR', 'Estimated shipping date cannot be in the past.', 'error');
    return;
  }

  shipping_dates.value = [...shipping_dates.value, formattedDate];

  let url = `customer-orders/${props.orderItem.id}/est-shipping-date`;

  try {
    await http.post(url, { shipping_dates: shipping_dates.value, ...reasonPayload });

    if (notify) {
      let url = `notify-customer-shipping-date/${props.orderItem.id}/order/${props.order.id}`;
      if (formattedDate) {
        await http.post(url);
        showAlert('Success', 'Order put in production and customer notified', 'success');
      } else {
        showAlert('error', 'Please save shipping date.', 'error');
      }
    }

    est_shipping_date.value = null;
    shipping_date_change_reason.value = '';
    shipping_date_change_reason_code.value = '';
    showAlert('Success', 'Order put in production', 'success');
    emit('putInProductionSaved', notify);
    closeEvent();
  } catch (errorResponse) {
    showAlert('ERROR', 'Failed to put order in production', 'error');
  }
};

const saveShippingDetails = async () => {
  const useSkipPath = skipSavingShippingDetails.value && allowSkipSavingShippingOption.value;

  if (useSkipPath) {
    const shippingData: ShippingDetailsFormData = {
      shipping_company: '',
      tracking_no: '',
      tracking_link: '',
      skip_saving_shipping_details: true,
    };
    if (props.submitEmitsOnly) {
      emit('submit', shippingData);
      shipping_company.value = '';
      tracking_no.value = '';
      tracking_link.value = '';
      mawb_number.value = '';
      mawb_airline_prefix.value = '';
      airlineHint.value = '';
      mawbError.value = '';
      lastMawbPrefixDigits.value = '';
      skipSavingShippingDetails.value = false;
      closeEvent();
      return;
    }
    emit('shippingDetailsSaved', shippingData);
    shipping_company.value = '';
    tracking_no.value = '';
    tracking_link.value = '';
    mawb_number.value = '';
    mawb_airline_prefix.value = '';
    airlineHint.value = '';
    mawbError.value = '';
    lastMawbPrefixDigits.value = '';
    skipSavingShippingDetails.value = false;
    showAlert(
      'Success',
      props.editShippingLink
        ? 'Tracking update skipped'
        : 'Order will proceed without saving tracking details',
      'success'
    );
    closeEvent();
    return;
  }

  if (!shipping_company.value) {
    showToast('Please select a shipping company', 'error');
    return;
  }

  if (!tracking_no.value || tracking_no.value.trim().length === 0) {
    showToast('Please enter a tracking number', 'error');
    return;
  }

  if (!tracking_link.value || tracking_link.value.trim().length === 0) {
    showToast('Please enter a tracking link', 'error');
    return;
  }

  if (mawb_number.value && !MAWB_REGEX.test(mawb_number.value)) {
    showToast('MAWB format must be: 3 digits, hyphen, 8 digits (e.g. 235-12345678)', 'error');
    return;
  }

  try {
    const resolvedAirlineName = mawb_airline_prefix.value
      ? (mawb_airlines.value.find((a) => a.prefix === mawb_airline_prefix.value)?.name ?? null)
      : null;

    const shippingData: ShippingDetailsFormData = {
      shipping_company: shipping_company.value,
      tracking_no: tracking_no.value,
      tracking_link: tracking_link.value || '',
      mawb_number: mawb_number.value || null,
      mawb_airline: resolvedAirlineName,
    };

    if (props.submitEmitsOnly) {
      emit('submit', shippingData);
      shipping_company.value = '';
      tracking_no.value = '';
      tracking_link.value = '';
      mawb_number.value = '';
      mawb_airline_prefix.value = '';
      airlineHint.value = '';
      mawbError.value = '';
      lastMawbPrefixDigits.value = '';
      skipSavingShippingDetails.value = false;
      closeEvent();
      return;
    }

    emit('shippingDetailsSaved', shippingData);

    shipping_company.value = '';
    tracking_no.value = '';
    tracking_link.value = '';
    mawb_number.value = '';
    mawb_airline_prefix.value = '';
    airlineHint.value = '';
    mawbError.value = '';
    lastMawbPrefixDigits.value = '';
    skipSavingShippingDetails.value = false;

    showAlert('Success', 'Shipping details saved successfully', 'success');
    closeEvent();
  } catch (errorResponse) {
    showAlert('ERROR', 'Failed to save shipping details', 'error');
  }
};
</script>
<style lang="sass" scoped></style>
